import * as z from "zod";

/**
 * What a workspace link may point to, mirrored from the API's WorkspaceLinkRules:
 * an absolute http(s) URL. The client is a little friendlier than the API — a
 * bare host such as "www.retsinformation.dk" gets "https://" prepended — but the
 * result always satisfies the API rule, so a form value that passes here is
 * never refused server-side for its shape.
 */

/** Mirrors WorkspaceLinkRules.MaxNameLength on the API. */
export const LINK_NAME_MAX_LENGTH = 200;
/** Mirrors WorkspaceLinkRules.MaxUrlLength on the API. */
export const LINK_URL_MAX_LENGTH = 2048;

export const LINK_NAME_REQUIRED_MESSAGE = "Udfyld navn";
export const LINK_URL_REQUIRED_MESSAGE = "Udfyld webadresse";
export const LINK_URL_INVALID_MESSAGE =
  "Indtast en gyldig webadresse, f.eks. https://www.retsinformation.dk";

const HTTP_PROTOCOLS = new Set(["http:", "https:"]);

/** "scheme:rest" where the scheme is letters/digits/+.- — the URL grammar's scheme. */
const SCHEME_PATTERN = /^([a-zA-Z][a-zA-Z0-9+.-]*):(.*)$/;

const parseUrl = (candidate: string): URL | null => {
  try {
    return new URL(candidate);
  } catch {
    return null;
  }
};

/**
 * The address as it should be sent to the API, or null when the input is not
 * (and cannot become) an absolute http(s) URL.
 *
 * - trims whitespace
 * - keeps an explicit http(s) URL as typed
 * - prepends "https://" when no scheme is given ("www.x.dk", "x.dk:8080/a")
 * - refuses every other scheme (javascript:, data:, mailto:, ftp:, file:)
 */
export function normalizeLinkUrl(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed.length === 0 || trimmed.length > LINK_URL_MAX_LENGTH) return null;

  const scheme = SCHEME_PATTERN.exec(trimmed);
  // "example.com:8080/path" matches the scheme grammar too, but a scheme is
  // never followed by digits only — that is a host with a port.
  const hasRealScheme = scheme !== null && !/^\d+([/?#]|$)/.test(scheme[2]);

  if (hasRealScheme) {
    const url = parseUrl(trimmed);
    if (
      !url ||
      !HTTP_PROTOCOLS.has(url.protocol) ||
      url.hostname.length === 0
    ) {
      return null;
    }
    return trimmed;
  }

  const prefixed = `https://${trimmed}`;
  const url = parseUrl(prefixed);
  // A bare word ("foo") is almost certainly a typo, so an auto-prefixed host
  // must look like one; an explicit "https://intranet" above is still allowed.
  if (!url || !url.hostname.includes(".")) return null;
  return prefixed;
}

/** The host to show as a link's source, without a leading "www."; "" when unparsable. */
export function getLinkHostname(url: string): string {
  const parsed = parseUrl(url);
  if (!parsed) return "";
  return parsed.hostname.replace(/^www\./, "");
}

/** Client-side mirror of the API rule, with the same Danish messages the dialog shows. */
export const linkFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { message: LINK_NAME_REQUIRED_MESSAGE })
    .max(LINK_NAME_MAX_LENGTH, {
      message: `Navnet må højst være ${LINK_NAME_MAX_LENGTH} tegn`,
    }),
  url: z
    .string()
    .trim()
    .min(1, { message: LINK_URL_REQUIRED_MESSAGE })
    .refine((value) => normalizeLinkUrl(value) !== null, {
      message: LINK_URL_INVALID_MESSAGE,
    }),
});

export type LinkFormValues = z.infer<typeof linkFormSchema>;
